import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { TrendingUp, DollarSign, PieChart, BarChart3 } from 'lucide-react'
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

const COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

export default function Dashboard() {
  const navigate = useNavigate()

  const { data: metrics, isLoading: metricsLoading, isError: metricsError } = useQuery({
    queryKey: ['metrics', 'latest'],
    queryFn: () => api.getLatestMetrics(),
    retry: false,
  })

  const { data: aggregate, isLoading: aggregateLoading, isError: aggregateError } = useQuery({
    queryKey: ['aggregate', 'latest'],
    queryFn: () => api.getLatestAggregate(),
    retry: false,
  })

  const { data: snapshots } = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.getSnapshots(0, 5),
  })

  if (metricsLoading || aggregateLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!metrics || !aggregate || metricsError || aggregateError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Data Available</h2>
        <p className="text-gray-600 mb-4">Upload your first portfolio statement to get started.</p>
        <button onClick={() => navigate('/upload')} className="btn btn-primary">
          Upload Statement
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Portfolio Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(metrics.total_portfolio_value)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Securities</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(metrics.total_securities_value)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cash</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(metrics.total_cash)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <PieChart className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Positions</p>
              <p className="text-2xl font-bold text-gray-900">
                {aggregate.positions.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Allocation */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Asset Allocation</h2>
          {metrics.asset_allocation.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={metrics.asset_allocation}
                    dataKey="value"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ category, percentage }) => `${category}: ${formatPercent(percentage)}`}
                  >
                    {metrics.asset_allocation.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No allocation data available</p>
          )}
        </div>

        {/* Top Positions */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Positions</h2>
          {metrics.top_positions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-gray-500">Symbol</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-500">Value</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-500">P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.top_positions.slice(0, 5).map((position, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3">
                        <div>
                          <p className="font-medium text-gray-900">{position.symbol}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[150px]">
                            {position.description}
                          </p>
                        </div>
                      </td>
                      <td className="text-right py-3 font-medium">
                        {formatCurrency(position.value)}
                      </td>
                      <td className={`text-right py-3 font-medium ${
                        position.unrealized_pnl && position.unrealized_pnl > 0
                          ? 'text-green-600'
                          : position.unrealized_pnl && position.unrealized_pnl < 0
                          ? 'text-red-600'
                          : ''
                      }`}>
                        {position.unrealized_pnl ? formatCurrency(position.unrealized_pnl) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No positions data available</p>
          )}
        </div>
      </div>

      {/* Recent Snapshots */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Snapshots</h2>
          <button
            onClick={() => navigate('/snapshots')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View all
          </button>
        </div>
        {snapshots?.uploads.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Filename</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.uploads.map((snapshot) => (
                  <tr key={snapshot.id} className="border-b last:border-0">
                    <td className="py-3 text-sm text-gray-900">
                      {new Date(snapshot.uploaded_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-sm text-gray-600">{snapshot.filename}</td>
                    <td className="py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        snapshot.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : snapshot.status === 'failed'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {snapshot.status}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => navigate(`/portfolio/${snapshot.id}`)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No snapshots yet</p>
        )}
      </div>
    </div>
  )
}
